import type { DealStatus } from "@/api/client";

type BadgeVariant = "secondary" | "success" | "destructive" | "outline";

/** Deal date(time) fields surfaced in the Status row. */
type DealDateField =
  | "submission_timestamp"
  | "reviewed_at"
  | "closed_at"
  | "win_submitted_at"
  | "extension_requested_at"
  // CHP-410: the registration-expiry date (the field the expiring sweep treats
  // as expiry). A plain date, not a timestamp — formatDate handles both.
  | "target_close_date";

export interface DealStatusDateRow {
  label: string;
  field: DealDateField;
}

export interface DealStatusConfig {
  /** Verbose detail-view pill label (distinct from the compact list badge). */
  pillLabel: string;
  pillVariant: BadgeVariant;
  /** Date fields shown in the horizontal Status row, in order. */
  dates: DealStatusDateRow[];
  /** Reviewer's name — rendered for the manager viewer only. */
  showReviewer: boolean;
  /** Rationale block below the row, shown to both viewers. */
  rationale?: "review_reason" | "close";
}

/**
 * Per-status configuration for the redesigned deal detail Status section
 * (RACCPE-21). Drives both the BDM/PO and CM/RA/RM detail views; the
 * manager-only Reviewer field is gated by the viewer, not the map.
 *
 * CHP-410: the `live` row now shows the registration "Expiry date"
 * (target_close_date) after the Registration Date, for all viewers (the ruling
 * uses target_close_date — the field the expiring sweep already treats as
 * expiry — rather than a distinct protection-window date).
 */
/** Terminal statuses — no further transitions for any actor. Grid rows in
 * these states render a visibly DISABLED quick-action control (spec: the
 * lockout is shown, not hidden). */
export const TERMINAL_DEAL_STATUSES: readonly DealStatus[] = [
  "closed_won",
  "closed_lost",
  "denied",
];

export function isTerminalDealStatus(status: DealStatus): boolean {
  return TERMINAL_DEAL_STATUSES.includes(status);
}

/** Partner deal action (BDM/PO vantage), consumed by the detail drawer's
 * `initialAction` hook: request closed-won (win submit) or mark closed-lost.
 * The grid-level Quick Action column was removed (CHP-315 / CHP-330); the
 * close-deal action now lives only in the detail drawer. */
export type PartnerQuickAction = "submit_win" | "close_lost";

export const DEAL_DETAIL_STATUS: Record<DealStatus, DealStatusConfig> = {
  draft: {
    pillLabel: "Draft",
    pillVariant: "outline",
    dates: [],
    showReviewer: false,
  },
  pending_review: {
    pillLabel: "Deal Registration - in review",
    pillVariant: "secondary",
    dates: [
      { label: "Submission Date", field: "submission_timestamp" },
      // CHP-237: in-review states show the expiry alongside their own date,
      // same CHP-410 field semantics as the `live` row below.
      { label: "Expiry date", field: "target_close_date" },
    ],
    showReviewer: false,
  },
  extension_pending: {
    pillLabel: "Deal extension - in review",
    pillVariant: "secondary",
    dates: [
      { label: "Extension requested", field: "extension_requested_at" },
      { label: "Expiry date", field: "target_close_date" },
    ],
    showReviewer: false,
  },
  win_pending_review: {
    pillLabel: "Closed-won deal status - in review",
    pillVariant: "secondary",
    dates: [
      { label: "Closed-won status requested", field: "win_submitted_at" },
      { label: "Expiry date", field: "target_close_date" },
    ],
    showReviewer: false,
  },
  live: {
    pillLabel: "Live",
    pillVariant: "success",
    dates: [
      { label: "Registration Date", field: "submission_timestamp" },
      // CHP-410: expiry date after the registration date.
      { label: "Expiry date", field: "target_close_date" },
    ],
    showReviewer: true,
  },
  denied: {
    pillLabel: "Deal registration declined",
    pillVariant: "destructive",
    dates: [
      { label: "Registration Date", field: "submission_timestamp" },
      { label: "Decision Date", field: "reviewed_at" },
    ],
    showReviewer: true,
    rationale: "review_reason",
  },
  closed_won: {
    pillLabel: "Closed-Won",
    pillVariant: "success",
    dates: [
      { label: "Registration Date", field: "submission_timestamp" },
      { label: "Closed Date", field: "closed_at" },
    ],
    showReviewer: true,
  },
  closed_lost: {
    pillLabel: "Closed-Lost",
    pillVariant: "destructive",
    dates: [
      { label: "Registration Date", field: "submission_timestamp" },
      { label: "Closed Date", field: "closed_at" },
    ],
    showReviewer: false,
    rationale: "close",
  },
};
